import { useState, useCallback, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { MENSAJE_EXITO_ENVIO } from "../utils/CrearValidacionHojaQXConstants";
import { validarCorreo } from "../utils/ValidationUtils";
import PrestadorService from "../../../services/prestador/PrestadorService";
import { getWindows } from "../utils/CrearValidacionHojaQXWindows";
import { MODALIDADES_PSS } from "../utils/HospitalizacionConstant";
import HojasQuirurgicasService from "../../../services/hospitalizacion/HojasQuirurgicasService";
import { LOCAL_STORAGE } from "../../../utils/Constant";

const useCrearValidacionHojaQX = ({
  handleEnviarCoberturas,
  setShowCoberturasRequeridasAlert,
  setShowResultadoEnvioModal,
  setResultadoEnvio,
  pacienteIngresado,
  setShowModalEncuestaSatisfaccion,
  authUserData,
  updatePrestadorCorreo,
  consultarTipoPssNoMedicoData,
  coberturasMedicos,
  documentosAnexos,
  coberturasEnviadasSet,
  archivosEnviados,
  coberturasDuplicadasIds,
  coberturasCompletas,
  handleEliminarCoberturaModal,
  handleEliminarAnexoModal,
  abrirModalDocumento,
  setShowCirugiasLiberadasModal,
  setCirugiasLiberadasData,
  setShowConfirmarEnvioCoberturas,
  pertinenciaMedica = false,
}) => {
  const { numeroIngreso } = useParams();
  const history = useHistory();
  const [coberturasEnviadas, setCoberturasEnviadas] = useState(false);
  const [comentarioValidacion, setComentarioValidacion] = useState("");
  const [showCorreoValidacionModal, setShowCorreoValidacionModal] =
    useState(false);
  const [showModalEncuestaCes, setShowModalEncuestaCes] = useState(false);
  const [showEncuestaCesIframe, setShowEncuestaCesIframe] = useState(false);
  const [datosEncuestaCes, setDatosEncuestaCes] = useState(null);

  const actualizarEnvioHojaQxEnBackend = useCallback(
    async (secuencial, numeroIngreso) => {
      if (!secuencial || !numeroIngreso) {
        return;
      }

      try {
        const nombreUsuario = authUserData?.prestador?.nombre || "";
        await HojasQuirurgicasService.actualizarEnvioHojaQx({
          requestData: {
            secuencial,
            numeroIngreso,
            enviado: "S",
            usuario: nombreUsuario,
          },
          requestHeaders: {},
        });
      } catch (error) {
        console.error("Error al actualizar estatus de envío de hoja quirúrgica:", error);
      }
    },
    [authUserData?.prestador?.nombre]
  );

  const procesarEnvioCoberturas = useCallback(
    async (correoParaEnvio) => {
      const resultado = await handleEnviarCoberturas(
        correoParaEnvio,
        comentarioValidacion,
        pertinenciaMedica
      );

      if (resultado?.requiereCoberturas) {
        setShowCoberturasRequeridasAlert(true);
        return;
      }

      if (resultado?.error) {
        setResultadoEnvio(resultado.resultadoEnvio);
        setShowResultadoEnvioModal(true);
        return;
      }

      const todasCoberturasYaEnviadas =
        resultado?.todasCoberturasYaEnviadas === true;

      if (todasCoberturasYaEnviadas) {
        return;
      }

      const coberturasLiberadas = resultado?.coberturasLiberadas || [];
      
      if (coberturasLiberadas.length > 0) {
        const nombresCoberturas = coberturasLiberadas.map(
          (c) => c?.cobertura?.nombreCobertura || c?.cobertura?.cobertura || "N/A"
        );

        let mensaje = "";
        if (coberturasCompletas.length === 1 && coberturasLiberadas.length === 1) {
          mensaje = "La cirugía se encuentra liberada y no requiere ser procesada vía área médica.";
        } else {
          mensaje = `Las siguientes cirugías se encuentran liberadas y no requieren ser procesadas vía área médica.`;
        }

        setCirugiasLiberadasData({
          mensaje,
          nombresCoberturas,
          onConfirm: async () => {
            setShowCirugiasLiberadasModal(false);
            
            if (resultado?.debeMostrarModalEncuesta) {
              setDatosEncuestaCes({
                secuencial: resultado.secuencial,
                numeroIngreso: resultado.numeroIngreso,
                nombreUsuario: resultado.nombreUsuario,
              });
              setShowModalEncuestaCes(true);
            } else {
              setCoberturasEnviadas(true);
              setResultadoEnvio(MENSAJE_EXITO_ENVIO);
              setShowResultadoEnvioModal(true);
            }
          },
        });
        setShowCirugiasLiberadasModal(true);
        return;
      }

      if (resultado?.debeMostrarModalEncuesta) {
        setDatosEncuestaCes({
          secuencial: resultado.secuencial,
          numeroIngreso: resultado.numeroIngreso,
          nombreUsuario: resultado.nombreUsuario,
        });
        setShowModalEncuestaCes(true);
      } else {
        setCoberturasEnviadas(true);
        setResultadoEnvio(MENSAJE_EXITO_ENVIO);
        setShowResultadoEnvioModal(true);
      }
    },
    [
      comentarioValidacion,
      handleEnviarCoberturas,
      pertinenciaMedica,
      setResultadoEnvio,
      setShowCoberturasRequeridasAlert,
      setShowResultadoEnvioModal,
      coberturasCompletas,
      setShowCirugiasLiberadasModal,
      setCirugiasLiberadasData,
    ]
  );

  const handleEnviarCoberturasClick = useCallback(() => {
    setShowConfirmarEnvioCoberturas(true);
  }, [setShowConfirmarEnvioCoberturas]);

  const handleConfirmarEnvioCoberturas = useCallback(async () => {
    setShowConfirmarEnvioCoberturas(false);
    
    if (pertinenciaMedica && (!comentarioValidacion || comentarioValidacion.trim() === "")) {
      setResultadoEnvio({
        tipo: "error",
        titulo: "Validación de pertinencia médica requerida",
        mensaje: "Por favor, indique en el campo de comentario, el diagnóstico específico que requiere validar. Esta información es indispensable para procesar correctamente la solicitud.",
      });
      setShowResultadoEnvioModal(true);
      return;
    }
    
    const correoElectronico = authUserData?.prestador?.correoElectronico;
    const errorCorreo = validarCorreo(correoElectronico || "");
    
    if (errorCorreo) {
      setShowCorreoValidacionModal(true);
      return;
    }

    await procesarEnvioCoberturas(correoElectronico);
  }, [
    authUserData?.prestador?.correoElectronico,
    procesarEnvioCoberturas,
    setShowCorreoValidacionModal,
    setShowConfirmarEnvioCoberturas,
    pertinenciaMedica,
    comentarioValidacion,
    setResultadoEnvio,
    setShowResultadoEnvioModal,
  ]);

  const actualizarCorreoPrestador = useCallback(
    async (correo) => {
      const request = {
        requestData: {
          correo: correo,
          comentario:
            "Actualización de correo electrónico desde creación de validación de hoja quirúrgica",
        },
        requestHeaders: {},
      };

      await PrestadorService.actualizarCorreoPrestador(request);
      
      if (updatePrestadorCorreo) {
        updatePrestadorCorreo(correo);
      }
    },
    [updatePrestadorCorreo]
  );

  const handleConfirmarCorreoValidacion = useCallback(
    async (correoValidacion) => {
      const errorCorreo = validarCorreo(correoValidacion || "");
      if (errorCorreo) {
        return;
      }

      try {
        await actualizarCorreoPrestador(correoValidacion);
        setShowCorreoValidacionModal(false);
        await procesarEnvioCoberturas(correoValidacion);
      } catch (err) {
        console.error("Error al actualizar correo del prestador:", err);
        
        setResultadoEnvio({
          tipo: "warning",
          titulo: "Advertencia",
          mensaje:
            "El correo se utilizará para esta validación, pero no se pudo actualizar en su perfil. Por favor, actualice su correo desde su información de prestador.",
          detalles: [
            {
              cobertura: "N/A",
              error:
                err?.response?.data?.mensaje ||
                err?.message ||
                "Error desconocido al actualizar correo",
            },
          ],
        });

        setShowCorreoValidacionModal(false);
        await procesarEnvioCoberturas(correoValidacion);
      }
    },
    [
      procesarEnvioCoberturas,
      actualizarCorreoPrestador,
      setResultadoEnvio,
    ]
  );

  const handleCerrarCorreoValidacionModal = useCallback(() => {
    setShowCorreoValidacionModal(false);
  }, []);

  const handleCerrarResultadoEnvioModal = useCallback(() => {
    setShowResultadoEnvioModal(false);
  }, [setShowResultadoEnvioModal]);

  const handleIrADetalles = useCallback(() => {
    setShowResultadoEnvioModal(false);
    
    // Notificar a la ventana padre que se guardó la validación
    try {
      localStorage.setItem('validacionHojaQXGuardada', JSON.stringify({
        numeroIngreso: numeroIngreso,
        timestamp: new Date().toISOString(),
      }));
      // Limpiar después de 1 segundo
      setTimeout(() => {
        localStorage.removeItem('validacionHojaQXGuardada');
      }, 1000);
    } catch (error) {
      console.error('Error al notificar validación:', error);
    }
    
    history.push(`/paciente-ingresado/${numeroIngreso}`);
  }, [history, numeroIngreso, setShowResultadoEnvioModal]);

  const handleVolverIngresosClick = useCallback(() => {
    // Notificar a la ventana padre que se guardó la validación
    try {
      localStorage.setItem('validacionHojaQXGuardada', JSON.stringify({
        numeroIngreso: numeroIngreso,
        timestamp: new Date().toISOString(),
      }));
      // Limpiar después de 1 segundo
      setTimeout(() => {
        localStorage.removeItem('validacionHojaQXGuardada');
      }, 1000);
    } catch (error) {
      console.error('Error al notificar validación:', error);
    }
    
    history.push("/pacientes-ingresados");
  }, [history, numeroIngreso]);

  const guardarFechaUltimaEncuesta = useCallback((codigoUsuario) => {
    if (!codigoUsuario) {
      return;
    }

    const fechaUltimaEncuestaData = {
      codigoUsuario: codigoUsuario,
      fechaHora: new Date().toISOString(),
    };
    const storageKey = `${LOCAL_STORAGE.FECHA_ULTIMA_ENCUESTA.KEY}_${codigoUsuario}`;
    localStorage.setItem(
      storageKey,
      JSON.stringify(fechaUltimaEncuestaData)
    );
  }, []);

  const actualizarEncuestaCesEnBackend = useCallback(
    async (encuestaCes) => {
      if (!datosEncuestaCes) {
        return;
      }

      try {
        await HojasQuirurgicasService.actualizarEncuestaCes({
          requestData: {
            secuencial: datosEncuestaCes.secuencial,
            numeroIngreso: datosEncuestaCes.numeroIngreso,
            encuestaCes: encuestaCes,
            usuario: datosEncuestaCes.nombreUsuario,
          },
          requestHeaders: {},
        });

        const codigoUsuario = authUserData?.prestador?.codigo;
        guardarFechaUltimaEncuesta(codigoUsuario);
      } catch (error) {
        console.error("Error al enviar encuesta CES:", error);
      }
    },
    [datosEncuestaCes, authUserData?.prestador?.codigo, guardarFechaUltimaEncuesta]
  );

  const handleResponderEncuesta = useCallback(
    (respuesta) => {
      console.log("Respuesta encuesta de satisfacción:", respuesta);
      if (setShowModalEncuestaSatisfaccion) {
        setShowModalEncuestaSatisfaccion(false);
      }
      setResultadoEnvio(MENSAJE_EXITO_ENVIO);
      setShowResultadoEnvioModal(true);
    },
    [
      setShowResultadoEnvioModal,
      setResultadoEnvio,
      setShowModalEncuestaSatisfaccion,
    ]
  );

  const handleResponderEncuestaCes = useCallback(
    async (respuesta) => {
      if (respuesta === "Sí") {
        setShowModalEncuestaCes(false);
        setTimeout(() => {
          setShowEncuestaCesIframe(true);
          setShowModalEncuestaCes(true);
        }, 100);
      } else {
        setShowModalEncuestaCes(false);
        setShowEncuestaCesIframe(false);
        
        await actualizarEncuestaCesEnBackend("N");

        if (datosEncuestaCes?.secuencial && datosEncuestaCes?.numeroIngreso) {
          await actualizarEnvioHojaQxEnBackend(datosEncuestaCes.secuencial, datosEncuestaCes.numeroIngreso);
        }

        setCoberturasEnviadas(true);
        setResultadoEnvio(MENSAJE_EXITO_ENVIO);
        setShowResultadoEnvioModal(true);
      }
    },
    [
      actualizarEncuestaCesEnBackend,
      actualizarEnvioHojaQxEnBackend,
      datosEncuestaCes,
      setShowResultadoEnvioModal,
      setResultadoEnvio,
    ]
  );

  const handleBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const tipoReclamante = useMemo(
    () => consultarTipoPssNoMedicoData?.tipoReclamante || MODALIDADES_PSS.ARS,
    [consultarTipoPssNoMedicoData]
  );

  const windows = useMemo(
    () =>
      getWindows({
        coberturasMedicos,
        documentosAnexos,
        abrirModalDocumento,
        archivosEnviados,
        coberturasEnviadas: coberturasEnviadasSet,
        coberturasDuplicadasIds,
        coberturasCompletas,
        handleEliminarCobertura: handleEliminarCoberturaModal,
        handleEliminarAnexo: handleEliminarAnexoModal,
        coberturasEnviadasFlag: coberturasEnviadas,
        tipoReclamante,
      }),
    [
      coberturasMedicos,
      documentosAnexos,
      abrirModalDocumento,
      archivosEnviados,
      coberturasEnviadasSet,
      coberturasDuplicadasIds,
      coberturasCompletas,
      handleEliminarCoberturaModal,
      handleEliminarAnexoModal,
      coberturasEnviadas,
      tipoReclamante,
    ]
  );

  const handleComentarioChange = useCallback((value) => {
    setComentarioValidacion(value);
  }, []);

  useEffect(() => {
    setCoberturasEnviadas(false);
    setComentarioValidacion("");
  }, [numeroIngreso]);

  useEffect(() => {
    if (pacienteIngresado?.tieneHojaQx === "S") {
      history.push("/dashboard");
    }
  }, [pacienteIngresado?.tieneHojaQx, history]);

  const handleAbrirEncuestaCes = useCallback(() => {
    setShowModalEncuestaCes(true);
    setShowEncuestaCesIframe(true);
  }, []);

  const handleCerrarEncuestaCesIframe = useCallback(async () => {
    setShowModalEncuestaCes(false);
    setShowEncuestaCesIframe(false);
    
    await actualizarEncuestaCesEnBackend("S");

    if (datosEncuestaCes?.secuencial && datosEncuestaCes?.numeroIngreso) {
      await actualizarEnvioHojaQxEnBackend(datosEncuestaCes.secuencial, datosEncuestaCes.numeroIngreso);
    }

    setCoberturasEnviadas(true);
    setResultadoEnvio(MENSAJE_EXITO_ENVIO);
    setShowResultadoEnvioModal(true);
  }, [actualizarEncuestaCesEnBackend, actualizarEnvioHojaQxEnBackend, datosEncuestaCes, setShowResultadoEnvioModal, setResultadoEnvio]);

  return {
    coberturasEnviadas,
    handleEnviarCoberturasClick,
    handleConfirmarEnvioCoberturas,
    showCorreoValidacionModal,
    handleConfirmarCorreoValidacion,
    handleCerrarCorreoValidacionModal,
    handleIrADetalles,
    handleVolverIngresosClick,
    handleResponderEncuesta,
    comentarioValidacion,
    handleComentarioChange,
    handleBack,
    tipoReclamante,
    windows,
    showModalEncuestaCes,
    showEncuestaCesIframe,
    handleResponderEncuestaCes,
    handleAbrirEncuestaCes,
    handleCerrarEncuestaCesIframe,
    handleCerrarResultadoEnvioModal,
  };
};

export default useCrearValidacionHojaQX;
